import crossFetch from 'cross-fetch';
import { DocsOwnershipApiResponse, DocsOwnershipQueryFilter } from '../types';
import { DocsOwnershipApi } from './DocsOwnershipApi';
import { ResponseError } from '@backstage/errors';
import {
  stringifyEntityRef,
  parseEntityRef,
  CompoundEntityRef,
} from '@backstage/catalog-model';
import { DEFAULT_NAMESPACE } from '@appfolio/backstage-plugin-catalog-common';

// these are isomorphic types that don't require @backstage/core-plugin-api
type DiscoveryApi = { getBaseUrl(pluginId: string): Promise<string> };
type FetchApi = { fetch: typeof fetch };

export class DocsOwnershipClient implements DocsOwnershipApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  public constructor(deps: {
    discoveryApi: DiscoveryApi;
    fetchApi?: FetchApi;
  }) {
    const { discoveryApi, fetchApi } = deps;
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi ?? { fetch: crossFetch };
  }

  public async getDocPathOwnerRef(entityRef: CompoundEntityRef, path: string) {
    const results = await this.getDocsOwnershipResults([
      {
        entityRef,
        paths: [path],
      },
    ]);
    return results.length && results[0].docs.length
      ? results[0].docs[0].ownerRef
      : null;
  }

  public async getDocsOwnershipResults(filters: DocsOwnershipQueryFilter[]) {
    const baseUrl = await this.discoveryApi.getBaseUrl('docs-ownership');
    const response = await this.fetchApi.fetch(
      new URL(`${baseUrl}/query`).toString(),
      {
        method: 'POST',
        body: JSON.stringify(
          filters.map(({ entityRef, ...filter }) => ({
            ...filter,
            entityRef: entityRef !== '*' ? stringifyEntityRef(entityRef) : '*',
          })),
        ),
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const body: DocsOwnershipApiResponse = await response.json();
    return body.results.map(({ entityRef, docs }) => ({
      entityRef: parseEntityRef(entityRef, {
        defaultKind: 'component',
        defaultNamespace: DEFAULT_NAMESPACE,
      }),
      docs: docs.map(({ ownerRef, ...doc }) => ({
        ...doc,
        ownerRef: parseEntityRef(ownerRef, {
          defaultKind: 'group',
          defaultNamespace: DEFAULT_NAMESPACE,
        }),
      })),
    }));
  }
}
